import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { ComboboxComponent } from "@components/combobox/combobox.component";
import { InputComponent } from "@components/input/input.component";
import { SelectComponent } from "@components/select/select.component";
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { ApiService, UserInfo } from "app/services/api.service";
import { take } from "rxjs";
import { SelectValue } from '../../../interfaces/select-value.interface'

@Component({
  selector: 'app-opportunities',
  templateUrl: './opportunities.component.html',
  styleUrl: './opportunities.component.scss',
  imports: [CommonModule, TranslateModule, FormsModule, SelectComponent, ButtonComponent, ComboboxComponent, CheckboxComponent, InputComponent],
  standalone: true,
})

export class OpportunitiesComponent implements OnInit {
    userInfo: UserInfo;

    configuration: any;
    originalSelectedConfigs: Set<string> = new Set();
    selectedConfigs: Set<string> = new Set();
    disabledConfigs: Set<string> = new Set();

    configurationOptions = [
        { id: 'cocina', label: 'my_profile.my_opportunities.configurations.configuration1', code: '0015' },
        { id: 'encimera', label: 'my_profile.my_opportunities.configurations.configuration2', code: '0013' },
        { id: 'baño', label: 'my_profile.my_opportunities.configurations.configuration3', code: '0016' }
    ];

    reason: any;
    selectedReason: string | null = null;
    reasonOptions = [
        { label: 'my_profile.my_opportunities.reasons.reason1', value: 'temporarily-close', id: 'temporarily-close' },
        { label: 'my_profile.my_opportunities.reasons.reason2', value: 'no-understand', id: 'no-understand' },
        { label: 'my_profile.my_opportunities.reasons.reason3', value: 'no-respond', id: 'no-respond' },
        { label: 'my_profile.my_opportunities.reasons.reason4', value: 'no-answer', id: 'no-answer' },
        { label: 'my_profile.my_opportunities.reasons.reason5', value: 'no-customers', id: 'no-customers' },
        { label: 'my_profile.my_opportunities.reasons.reason6', value: 'no-contact', id: 'no-contact' },
    ];
    selectedReasonOption: SelectValue | null = null;

    startDate: string = new Date().toISOString().split('T')[0];
    minDate: string = this.startDate;
    endDate: string | null = null;
    minEndDate: string | null = this.calculateMinEndDate(this.startDate);

    subject: string = '';

    constructor(private api: ApiService, private translate: TranslateService) {}

    private translations(){
        this.translate.get([
            'my_profile.my_opportunities.reasons.reason1',
            'my_profile.my_opportunities.reasons.reason2',
            'my_profile.my_opportunities.reasons.reason3',
            'my_profile.my_opportunities.reasons.reason4',
            'my_profile.my_opportunities.reasons.reason5',
            'my_profile.my_opportunities.reasons.reason6'
        ])
    }

    ngOnInit(): void {
        this.api.userInfo.pipe(take(1)).subscribe((userInfo) => {
            this.userInfo = userInfo;

            this.api.getAccountSelectedServices().subscribe({
                next: (res) => {
                    this.setSelectedConfigsFromPayload(res.services);
                },
                error: (err) => {
                    console.error('Error al obtener servicios seleccionados:', err);
                }
            });

        });
    }

    private setSelectedConfigsFromPayload(payload: string): void {
        if (!payload) return;

        const codes = payload.split(';');

        const selectedIds = this.configurationOptions
            .filter(option => codes.includes(option.code))
            .map(option => option.id);

        selectedIds.forEach(id => {
            this.selectedConfigs.add(id);
            this.originalSelectedConfigs.add(id);
        });

        // Solo aplicar restricción si NO están ambos seleccionados (caso legacy)
        const hasCocina = this.selectedConfigs.has('cocina');
        const hasEncimera = this.selectedConfigs.has('encimera');

        if (hasCocina && !hasEncimera) {
            this.disabledConfigs.add('encimera');
        } else if (hasEncimera && !hasCocina) {
            this.disabledConfigs.add('cocina');
        }
        // Si ambos están seleccionados, no deshabilitamos ninguno para permitir deseleccionar
    }

    onConfigChange(id: string, checked: boolean): void {
        if (checked) {
            this.selectedConfigs.add(id);
        } else {
            this.selectedConfigs.delete(id);
        }

        // Recalcular restricciones basándose en el estado actual
        const hasCocina = this.selectedConfigs.has('cocina');
        const hasEncimera = this.selectedConfigs.has('encimera');

        if (hasCocina && !hasEncimera) {
            this.disabledConfigs.add('encimera');
            this.disabledConfigs.delete('cocina');
        } else if (hasEncimera && !hasCocina) {
            this.disabledConfigs.add('cocina');
            this.disabledConfigs.delete('encimera');
        } else {
            // Si ambos o ninguno están seleccionados, no hay restricción
            this.disabledConfigs.delete('cocina');
            this.disabledConfigs.delete('encimera');
        }
    }

    get isConfigModified(): boolean {
        if (this.selectedConfigs.size !== this.originalSelectedConfigs.size) return true;

        for (const id of this.selectedConfigs) {
            if (!this.originalSelectedConfigs.has(id)) return true;
        }

        return false;
    }

    onSaveConfigurations(): void {
        const selectedCodes = this.configurationOptions
            .filter(option => this.selectedConfigs.has(option.id))
            .map(option => option.code);

        const payload = selectedCodes.join(';');

        this.api.updateAccountSelectedServices(payload).subscribe({
            next: (success) => {
                if (success) {
                    console.log('Servicios actualizados correctamente:', payload);
                    this.originalSelectedConfigs = new Set(this.selectedConfigs);                    
                } else {
                    console.warn('No se pudo actualizar la configuración');
                }
            },
            error: (err) => {
                console.error('Error al actualizar servicios:', err);
            }
        });
    }

    onReasonChange(reason: SelectValue | null): void {
        this.selectedReason = reason?.value;
        this.selectedReasonOption = reason;

        if (reason?.value !== 'temporarily-close') {
            this.endDate = null;
        }
    }

    private mapReason(reason: string): string {
        const reasonMap: Record<string, string> = {
            'temporarily-close': 'I’m temporarily closing the store',
            'no-understand': 'I don’t understand how the platform works',
            'no-respond': 'I’m unable to respond to our customers in a timely manner',
            'no-answer': 'Customers don’t answer my calls or emails',
            'no-customers': 'I don’t get many customers through this channel',
            'no-contact': 'Once the quote has been sent, the customer does not contact me again'
        };

        return reasonMap[reason] || reason;
    }

    get isUnsubscribeDisabled(): boolean {
        if (!this.selectedReason) return true;
        if (this.selectedReason === 'temporarily-close' && !this.endDate) return true;
        return false;
    }

    private calculateMinEndDate(start: string): string {
        const date = new Date(start);
        date.setDate(date.getDate() + 1);
        return date.toISOString().split('T')[0];
    }

    onStartDateChange(date: string): void {
        this.startDate = date;
        this.minEndDate = this.calculateMinEndDate(date);

        if (this.endDate && this.endDate < this.minEndDate) {
            this.endDate = null;
        }
    }

    onUnsubscribe(): void {
        if (!this.selectedReason || !this.startDate) {
            console.warn('Motivo y fecha de inicio son obligatorios');
            return;
        }

        const today = new Date();
        const formatDate = (date: any): string =>
            new Date(date || today).toISOString().split('T')[0];

        const payload: {
            reason: string;
            startDate: string;
            endDate?: string;
            comment?: string;
        } = {
            reason: this.mapReason(this.selectedReason),
            startDate: formatDate(this.startDate),
            comment: this.subject || ''
        };

        if (this.selectedReason === 'temporarily-close' && this.endDate) {
            payload.endDate = formatDate(this.endDate);
        }

        console.log('Payload de baja:', payload);

        this.api.unsubscribeFromLeads(payload).subscribe({
            next: (success) => {
            if (success) {
                console.log('Dado de baja correctamente');
                this.resetUnsubscribeForm();
            } else {
                console.warn('No se pudo procesar la baja');
            }
            },
            error: (err) => {
            console.error('Error al darse de baja:', err);
            }
        });
    }

    resetUnsubscribeForm(): void {
        this.selectedReason = null;
        this.startDate = new Date().toISOString().split('T')[0];
        this.endDate = null;
        this.subject = '';
    }
}